import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Backdrop,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	InputAdornment,
	Link,
	MenuItem,
	Pagination,
	Paper,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CreateIcon from '@mui/icons-material/Create';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axios from 'axios';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';
import routes from 'constants/routes';

import { pageSizeByDefault, pageSizeOptions } from 'constants/constants';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { defaultAlertState } from 'features/common/constants';
import { convertFiltersToRecord } from 'features/common/helpers';
import { useDebounceState } from 'features/common/hooks/useDebounceState';
import { FilterModel, IHeadCell, ISort, ShowAlertState } from 'features/common/types';
import { extractErrorMessage } from 'features/quotation/helpers';

import { getProviderById, getProviderList } from '../../services';
import { IProviderDTO, IProviderListDTO } from '../../types';
import {
	buildProvidersListSearch,
	normalizeProvidersListSearch,
	parseProvidersListState,
	PROVIDERS_LIST_DEFAULT_SORT,
	PROVIDERS_LIST_STATE_KEY,
	providersListPathWithSearch,
	readProvidersListSearch,
	saveProvidersListSearch,
	toProvidersListSearchParam,
} from '../../helpers/providersListState';
import { ProvidersFilters } from './ProvidersFilters';
import { ProvidersAdvancedSearch } from './ProvidersAdvancedSearch';
import { useStyles } from './styles';
import { ProviderEditDrawer } from '../ProviderEditDrawer';
import ProviderPaymentDateRuleModal from '../ProviderPaymentDateRuleModal';
import ProviderPaymentCalendarViewConfigModal from '../ProviderPaymentCalendarViewConfigModal';

const headerCells: IHeadCell[] = [
	{ field: 'fantasyName', sortable: true, headerName: 'Nombre fantasía' },
	{ field: 'businessName', sortable: true, headerName: 'Razón social' },
	{ field: 'rut', sortable: true, headerName: 'RUT' },
	{ field: 'countryName', sortable: true, headerName: 'País' },
	{ field: 'email', sortable: true, headerName: 'Email' },
	{ field: 'hasPaymentDateRule', sortable: true, headerName: 'Regla de pago' },
	{ field: 'enable', sortable: true, headerName: 'Estado' },
];

const DEFAULT_SORT = PROVIDERS_LIST_DEFAULT_SORT;

const formatOptionalValue = (value?: string | number | null): string => {
	if (value === undefined || value === null || value === '') return '—';
	return String(value);
};

export const ProvidersDataList = (): JSX.Element => {
	const classes = useStyles();
	const history = useHistory();
	const location = useLocation();
	const hasHydratedForVisitRef = useRef(false);
	const [isListHydrated, setIsListHydrated] = useState(false);

	const [loading, setLoading] = useState<boolean>(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [currentSize, setCurrentSize] = useState<number>(pageSizeByDefault);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchValue, setSearchValue] = useState<string>('');
	const [sortFields, setSortFields] = useState<ISort[]>(DEFAULT_SORT);
	const [data, setData] = useState<IProviderListDTO[]>([]);
	const [filterApplied, setFilterApplied] = useState<FilterModel[]>([]);
	const [advancedSearchProvider, setAdvancedSearchProvider] = useState<IProviderDTO | null>(null);
	const [advancedSearchProviderId, setAdvancedSearchProviderId] = useState<string | undefined>();

	const [editDrawerOpen, setEditDrawerOpen] = useState(false);
	const [editingProviderId, setEditingProviderId] = useState<string | undefined>();
	const [openRule, setOpenRule] = useState(false);
	const [openCalendarConfig, setOpenCalendarConfig] = useState(false);
	const [selectedProvider, setSelectedProvider] = useState<IProviderListDTO | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	const debouncedSearch = useDebounceState(searchValue, 300);
	const previousDebouncedSearchRef = useRef('');
	const pendingPageResetRef = useRef(false);

	const mergedFilters = useMemo(
		() => ({
			...convertFiltersToRecord(filterApplied),
			...(advancedSearchProviderId ? { id: advancedSearchProviderId } : {}),
		}),
		[filterApplied, advancedSearchProviderId],
	);

	const filtersKey = JSON.stringify(mergedFilters);
	const debouncedFiltersKey = useDebounceState(filtersKey, 300);

	const buildCurrentListSearch = useCallback((): string => {
		const queryString = buildProvidersListSearch({
			search: debouncedSearch,
			page: currentPage,
			size: currentSize,
			sortFields,
			filterApplied,
			advancedProviderId: advancedSearchProviderId,
		});
		return toProvidersListSearchParam(queryString);
	}, [debouncedSearch, currentPage, currentSize, sortFields, filterApplied, advancedSearchProviderId]);

	const navigateToProviderDetail = useCallback(
		(id: string) => {
			const listSearch = buildCurrentListSearch();

			saveProvidersListSearch(listSearch);
			window.history.replaceState(window.history.state, '', providersListPathWithSearch(listSearch));

			history.push({
				pathname: routes.providers.detail.replace(':id', id),
				state: { [PROVIDERS_LIST_STATE_KEY]: listSearch },
			});
		},
		[buildCurrentListSearch, history],
	);

	useEffect(() => {
		if (location.pathname !== routes.providers.list) {
			hasHydratedForVisitRef.current = false;
			setIsListHydrated(false);
			return;
		}

		if (hasHydratedForVisitRef.current) return;
		hasHydratedForVisitRef.current = true;

		let cancelled = false;
		const urlSearch = normalizeProvidersListSearch(
			typeof window !== 'undefined' ? window.location.search : location.search,
		);
		const storedSearch = normalizeProvidersListSearch(readProvidersListSearch());
		const parsed = parseProvidersListState(urlSearch || storedSearch);

		setSearchValue(parsed.search);
		setCurrentPage(parsed.page);
		setCurrentSize(parsed.size);
		setSortFields(parsed.sortFields);
		setFilterApplied(parsed.filterApplied);
		setAdvancedSearchProviderId(parsed.advancedProviderId);
		setAdvancedSearchProvider(null);
		setIsListHydrated(true);

		if (parsed.advancedProviderId) {
			getProviderById(parsed.advancedProviderId)
				.then(({ data }) => {
					if (!cancelled) {
						setAdvancedSearchProvider(data);
					}
				})
				.catch(() => {
					if (!cancelled) {
						setAdvancedSearchProvider(null);
					}
				});
		}

		return () => {
			cancelled = true;
		};
	}, [location.pathname]);

	useEffect(() => {
		if (!isListHydrated) return;

		const previousDebouncedSearch = previousDebouncedSearchRef.current;
		const searchChanged = previousDebouncedSearch !== debouncedSearch;
		previousDebouncedSearchRef.current = debouncedSearch;
		if (searchChanged && currentPage !== 1) {
			pendingPageResetRef.current = true;
			setCurrentPage(1);
			return;
		}

		const nextSearch = buildProvidersListSearch({
			search: debouncedSearch,
			page: currentPage,
			size: currentSize,
			sortFields,
			filterApplied,
			advancedProviderId: advancedSearchProviderId,
		});
		const listSearch = toProvidersListSearchParam(nextSearch);
		const currentSearch = normalizeProvidersListSearch(
			typeof window !== 'undefined' ? window.location.search : location.search,
		);
		if (nextSearch === currentSearch) return;

		saveProvidersListSearch(listSearch);
		window.history.replaceState(window.history.state, '', providersListPathWithSearch(listSearch));
	}, [debouncedSearch, currentPage, currentSize, sortFields, filterApplied, advancedSearchProviderId, isListHydrated]);

	useEffect(() => {
		if (!isListHydrated) return;
		if (pendingPageResetRef.current) {
			pendingPageResetRef.current = false;
			return;
		}

		const abortController = new AbortController();

		const loadData = async () => {
			try {
				setLoading(true);
				const { data: response } = await getProviderList(
					currentPage,
					currentSize,
					mergedFilters,
					debouncedSearch || undefined,
					sortFields,
					abortController.signal,
				);

				if (abortController.signal.aborted) return;

				const total = response.total ?? 0;
				setTotalRecords(total);
				setPageTotal(total === 0 ? 0 : Math.ceil(total / currentSize));
				setData(response.elements ?? []);
			} catch (error) {
				if (abortController.signal.aborted || axios.isCancel(error)) return;
				setAlert({
					show: true,
					severity: 'error',
					message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado'),
				});
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false);
				}
			}
		};

		loadData();

		return () => {
			abortController.abort();
		};
	}, [currentPage, currentSize, sortFields, debouncedSearch, debouncedFiltersKey, refreshKey, isListHydrated]);

	const handleRefresh = () => setRefreshKey((prev) => prev + 1);

	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
	};

	const handleFiltersChange = useCallback((filters: FilterModel[]) => {
		setFilterApplied(filters);
		setCurrentPage(1);
	}, []);

	const handleAdvancedSearchSelect = useCallback((provider: IProviderDTO) => {
		setAdvancedSearchProviderId(provider.id);
		setAdvancedSearchProvider(provider);
		setCurrentPage(1);
	}, []);

	const handleAdvancedSearchClear = useCallback(() => {
		setAdvancedSearchProviderId(undefined);
		setAdvancedSearchProvider(null);
		setCurrentPage(1);
	}, []);

	const handleColumnSort = (field: string) => {
		setSortFields((prev) => {
			const current = prev.find((item) => item.field === field);
			if (!current) return [{ field, order: 'asc' }];
			if (current.order === 'asc') return [{ field, order: 'desc' }];
			return DEFAULT_SORT;
		});
		setCurrentPage(1);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};

	const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	const handleOpenDetail = (id: string) => {
		navigateToProviderDetail(id);
	};

	const handleCreate = () => {
		setEditingProviderId(undefined);
		setEditDrawerOpen(true);
	};

	const handleEdit = (id: string) => (event: React.MouseEvent) => {
		event.stopPropagation();
		setEditingProviderId(id);
		setEditDrawerOpen(true);
	};

	const handleOpenRule = (provider: IProviderListDTO) => (event: React.MouseEvent) => {
		event.stopPropagation();
		setSelectedProvider(provider);
		setOpenRule(true);
	};

	const handleOpenCalendarConfig = (provider: IProviderListDTO) => (event: React.MouseEvent) => {
		event.stopPropagation();
		setSelectedProvider(provider);
		setOpenCalendarConfig(true);
	};

	const handleCloseAlert = () => setAlert(defaultAlertState);

	const getSortDirection = (field: string): 'asc' | 'desc' | false => {
		return sortFields.find((item) => item.field === field)?.order ?? false;
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading && data.length === 0}>
				<CircularProgress color="inherit" />
			</Backdrop>

			<Paper className={classes.filtersCard} elevation={0}>
				<Box className={classes.toolbarRow}>
					<TextField
						label="Buscar"
						placeholder="Buscar por nombre, razón social, RUT, email o país"
						variant="outlined"
						size="small"
						value={searchValue}
						onChange={handleSearchChange}
						sx={{ flex: 1, minWidth: 280 }}
						inputProps={{ autoComplete: 'off' }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon color="action" />
								</InputAdornment>
							),
						}}
					/>
					<Box className={classes.toolbarActions}>
						<Typography className={classes.resultsCount}>
							{totalRecords.toLocaleString('es-UY')} {totalRecords === 1 ? 'resultado' : 'resultados'}
						</Typography>
						<Tooltip title="Nuevo proveedor">
							<IconButton onClick={handleCreate} color="primary">
								<AddCircleIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
				<ProvidersAdvancedSearch
					selectedProvider={advancedSearchProvider}
					onSelect={handleAdvancedSearchSelect}
					onClear={handleAdvancedSearchClear}
				/>
			</Paper>

			<ProvidersFilters filters={filterApplied} onFiltersChange={handleFiltersChange} />

			<Paper className={classes.tablePaper} elevation={0}>
				<TableContainer>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								{headerCells.map((cell) =>
									cell.sortable ? (
										<TableCell
											key={cell.field}
											className={classes.tableHeader}
											sortDirection={getSortDirection(cell.field)}
										>
											<TableSortLabel
												active={sortFields.some((item) => item.field === cell.field)}
												sx={{
													color: 'white !important',
													'& .MuiTableSortLabel-icon': { color: 'white !important' },
													'&:hover': { color: 'rgba(255,255,255,0.85) !important' },
												}}
												direction={getSortDirection(cell.field) || 'asc'}
												onClick={() => handleColumnSort(cell.field)}
											>
												{cell.headerName}
											</TableSortLabel>
										</TableCell>
									) : (
										<TableCell key={cell.field} className={classes.tableHeader}>
											{cell.headerName}
										</TableCell>
									),
								)}
								<TableCell className={classes.tableHeader} align="right">
									Acciones
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{!data.length ? (
								<TableRow>
									<TableCell colSpan={headerCells.length + 1}>
										<PageNoResult />
									</TableCell>
								</TableRow>
							) : (
								data.map((item) => (
									<TableRow key={item.id} className={classes.tableRow} onClick={() => handleOpenDetail(item.id)}>
										<TableCell>
											<Link
												component={RouterLink}
												to={routes.providers.detail.replace(':id', item.id)}
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													navigateToProviderDetail(item.id);
												}}
											>
												{item.fantasyName}
											</Link>
										</TableCell>
										<TableCell>{item.businessName}</TableCell>
										<TableCell>{formatOptionalValue(item.rut)}</TableCell>
										<TableCell>{item.country?.name ?? '—'}</TableCell>
										<TableCell>{formatOptionalValue(item.email)}</TableCell>
										<TableCell>
											<Chip
												label={item.hasPaymentDateRule ? 'Configurada' : 'Sin regla'}
												size="small"
												color={item.hasPaymentDateRule ? 'success' : 'warning'}
												variant="outlined"
											/>
										</TableCell>
										<TableCell>
											<Chip
												label={item.enable ? 'Activo' : 'Inactivo'}
												size="small"
												color={item.enable ? 'success' : 'default'}
												variant="outlined"
											/>
										</TableCell>
										<TableCell align="right" className={classes.actionsCell}>
											<Tooltip title="Editar">
												<IconButton size="small" onClick={handleEdit(item.id)}>
													<CreateIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip
												title={
													item.hasPaymentDateRule
														? 'Modificar regla de fecha de pago'
														: 'Configurar regla de fecha de pago'
												}
											>
												<IconButton
													size="small"
													onClick={handleOpenRule(item)}
													color={item.hasPaymentDateRule ? 'primary' : 'error'}
												>
													<EventRepeatIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Configurar agrupación en calendario de pagos">
												<IconButton size="small" onClick={handleOpenCalendarConfig(item)} color="secondary">
													<CalendarMonthIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			{totalRecords > 0 && (
				<Box className={classes.paginationBar}>
					<Select value={currentSize} onChange={handleRowsPerPageChange} size="small">
						{pageSizeOptions.map((value) => (
							<MenuItem key={value} value={value}>
								{value} por página
							</MenuItem>
						))}
					</Select>
					<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
				</Box>
			)}

			<ProviderEditDrawer
				open={editDrawerOpen}
				providerId={editingProviderId}
				onClose={() => setEditDrawerOpen(false)}
				onSaved={handleRefresh}
			/>

			<ProviderPaymentDateRuleModal
				open={openRule}
				providerId={selectedProvider?.id || ''}
				providerFantasyName={selectedProvider?.fantasyName || ''}
				onClose={() => setOpenRule(false)}
				onSaved={handleRefresh}
			/>

			<ProviderPaymentCalendarViewConfigModal
				open={openCalendarConfig}
				providerId={selectedProvider?.id || ''}
				providerFantasyName={selectedProvider?.fantasyName || ''}
				onClose={() => {
					setSelectedProvider(null);
					setOpenCalendarConfig(false);
				}}
			/>

			<Snackbar open={alert.show} autoHideDuration={3000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity} onClose={handleCloseAlert}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
